import {Dispatch, UnknownAction} from '@reduxjs/toolkit';
import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Card} from 'platform/components';
import {Hide, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {appSyncApi} from '@dms/api/appsync';
import {
  ACCOUNTING_SUBJECT_VOCABULARY,
  BackgroundNotificationAccountingSubjectChangedPayload,
} from '@dms/api/core';
import {checkoutApi, useGetAccountingOperationQuery} from '@dms/api/sales';
import {notificationTypes} from '@dms/shared';

import {
  DEBUG_ACCOUNTING_MILESTONES,
  DebugAccountingMilestone,
  buildDebugAccountingOperation,
} from '../utils/buildDebugAccountingOperation';
import {
  AccountingDoorbellTarget,
  getAccountingDoorbellTarget,
} from '../utils/getAccountingDoorbellTarget';

const {contextTypes, reasons, sourceTypes} = ACCOUNTING_SUBJECT_VOCABULARY;
const FOREIGN_ORDER_ID = 'foreign-order-id';
const FOREIGN_CHECKOUT_ID = 'foreign-checkout-id';

/**
 * The app store declares its state without the RTK Query reducer paths, so the
 * default dispatch rejects a cache-patching thunk the store accepts at runtime.
 * Narrowing to this one thunk avoids touching the store typing.
 */
type AppSyncCacheDispatch = Dispatch<UnknownAction> & {
  (thunk: ReturnType<typeof appSyncApi.util.updateQueryData>): unknown;
  (thunk: ReturnType<typeof checkoutApi.util.upsertQueryData>): unknown;
};

interface OmneticCacheState {
  rtk_omneticApi?: {
    queries?: Record<string, {endpointName?: string; originalArgs?: unknown; data?: unknown}>;
  };
}

const buildSubjectChangedPayload = (
  target: AccountingDoorbellTarget,
  reason: BackgroundNotificationAccountingSubjectChangedPayload['reason']
): BackgroundNotificationAccountingSubjectChangedPayload => ({
  subject: {sourceType: sourceTypes.CHECKOUT_ORDER, sourceId: target.orderId},
  context: [
    {type: contextTypes.CHECKOUT_ID, id: target.checkoutId},
    {type: contextTypes.ORDER_ID, id: target.orderId},
    {type: contextTypes.PAYMENT_ID, id: target.paymentId},
  ],
  reason,
  documentId: null,
});

/**
 * Fires the external-accounting doorbell by hand.
 *
 * Under `AccountingMode::External` the backend rings a background notification
 * every time an issuance advances or accounting data changes the checkout on its
 * own, and the checkout screen reacts by refetching. Reproducing
 * that from the outside means issuing a real payment and waiting minutes for the
 * external accounting system to answer, which makes the frontend half
 * impractical to exercise by hand.
 *
 * This pushes a notification straight into the AppSync subscription's cache
 * entry — exactly what the socket does on a real push — so filtering, debouncing,
 * invalidation and the refetch all run for real.
 */
export function AccountingDoorbellDebug() {
  const dispatch = useDispatch<AppSyncCacheDispatch>();
  const [simulatedMilestone, setSimulatedMilestone] = useState<DebugAccountingMilestone | null>(
    null
  );

  const target = useSelector((state: OmneticCacheState) =>
    getAccountingDoorbellTarget(state.rtk_omneticApi?.queries)
  );

  const accountingOperation = useGetAccountingOperationQuery(target ?? skipToken);

  const ring = (
    reason: BackgroundNotificationAccountingSubjectChangedPayload['reason'],
    overrides: Partial<AccountingDoorbellTarget>
  ) => {
    if (isNil(target)) {
      return;
    }

    dispatch(
      appSyncApi.util.updateQueryData('subscribeToNotifications', undefined, (draft) => {
        draft.push({
          variant: 'background',
          id: `local-doorbell-${draft.length}`,
          type: notificationTypes.ACCOUNTING_SUBJECT_CHANGED,
          createdAt: new Date().toISOString(),
          isRead: false,
          payload: buildSubjectChangedPayload({...target, ...overrides}, reason),
        });
      })
    );
  };

  const handleRingTwice = () => {
    ring(reasons.ISSUANCE_CHANGED, {});
    ring(reasons.ISSUANCE_CHANGED, {});
  };

  const simulateNextMilestone = () => {
    if (isNil(target)) {
      return;
    }

    const currentIndex = isNotNil(simulatedMilestone)
      ? DEBUG_ACCOUNTING_MILESTONES.indexOf(simulatedMilestone)
      : -1;
    const nextMilestone =
      DEBUG_ACCOUNTING_MILESTONES.at((currentIndex + 1) % DEBUG_ACCOUNTING_MILESTONES.length) ??
      'waiting';

    setSimulatedMilestone(nextMilestone);
    dispatch(
      checkoutApi.util.upsertQueryData(
        'getAccountingOperation',
        target,
        buildDebugAccountingOperation(target.paymentId, nextMilestone)
      )
    );
  };

  return (
    <Card title="Accounting doorbell" variant="inlineGrey">
      <VStack spacing={2}>
        <Text size="xSmall" color="tertiary">
          Simulates the background notification that external accounting sends when an issuance
          advances or when its data changes the checkout (payment paid, document bound, printout
          attached). Open a business case checkout in the background first — the doorbell only
          listens while that screen is mounted, and only for tenants with the
          createExternalCheckoutDocuments permission.
        </Text>

        <Hide when={isNotNil(target)}>
          <Text size="xSmall" color="tertiary">
            No checkout order in the query cache. Open a business case checkout that has at least
            one payment, then reopen this panel.
          </Text>
        </Hide>

        <Show when={isNotNil(target)}>
          <Text size="xSmall" color="tertiary">
            {`Target — checkout ${target?.checkoutId} · order ${target?.orderId} · payment ${target?.paymentId}`}
          </Text>

          <Button
            title="Ring once"
            onClick={() => ring(reasons.ISSUANCE_CHANGED, {})}
            variant="secondary"
            data-testid="devSettings-accountingDoorbell-ringButton"
          />
          <Text size="xSmall" color="tertiary">
            One issuance milestone arrives. Expect one request each for the order, the checkout, the
            invoice payment lists, the business case and this payment&apos;s accounting operation.
          </Text>

          <Button
            title="Ring twice in a row"
            onClick={handleRingTwice}
            variant="secondary"
            data-testid="devSettings-accountingDoorbell-ringTwiceButton"
          />
          <Text size="xSmall" color="tertiary">
            A single issuance normally rings several times over minutes. Both of these land inside
            the debounce window, so each request must be sent once, not twice.
          </Text>

          <Button
            title="Ring payment paid"
            onClick={() => ring(reasons.PAYMENT_PAID, {})}
            variant="secondary"
            data-testid="devSettings-accountingDoorbell-ringPaymentPaidButton"
          />
          <Text size="xSmall" color="tertiary">
            A payment got paid in the external accounting. Expect the same requests as for one
            issuance milestone.
          </Text>

          <Button
            title="Ring for a foreign order"
            onClick={() => ring(reasons.ISSUANCE_CHANGED, {orderId: FOREIGN_ORDER_ID})}
            variant="secondary"
            data-testid="devSettings-accountingDoorbell-ringForeignButton"
          />
          <Button
            title="Ring for a foreign checkout"
            onClick={() => ring(reasons.ISSUANCE_CHANGED, {checkoutId: FOREIGN_CHECKOUT_ID})}
            variant="secondary"
            data-testid="devSettings-accountingDoorbell-ringForeignCheckoutButton"
          />
          <Text size="xSmall" color="tertiary">
            Data from the external accounting has no initiating user, so every user of the branch is
            notified and doorbells for other orders and checkouts do arrive. These must be dropped —
            no request at all.
          </Text>

          <Button
            title={`Simulate next state${isNotNil(simulatedMilestone) ? ` (now: ${simulatedMilestone})` : ''}`}
            onClick={simulateNextMilestone}
            variant="secondary"
            data-testid="devSettings-accountingDoorbell-simulateButton"
          />
          <Text size="xSmall" color="tertiary">
            Writes a fake accounting operation into the cache and steps through every state on each
            click, so the payment screen can be seen without the backend. A later refetch that fails
            leaves the fake in place; one that succeeds replaces it with the real state.
          </Text>

          <Text size="xSmall" color="tertiary">
            {`summaryState: ${accountingOperation.data?.accountingProcess.summaryState ?? '—'} · document: ${accountingOperation.data?.accountingProcess.document.state ?? '—'} · printout: ${accountingOperation.data?.accountingProcess.printout.state ?? '—'}`}
          </Text>
          <Text size="xSmall" color="tertiary">
            {`isFetching: ${accountingOperation.isFetching} · isError: ${accountingOperation.isError} · lastFulfilled: ${accountingOperation.fulfilledTimeStamp ?? '—'}`}
          </Text>
          <Text size="xSmall" color="tertiary">
            The readout above is this panel&apos;s own subscription to the accounting operation, so
            it moves on every refetch. The checkout screen itself repaints only once the accounting
            state UI renders it.
          </Text>
        </Show>
      </VStack>
    </Card>
  );
}
